import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { HiOutlineBuildingStorefront } from "react-icons/hi2";

import {
  communityUrl,
  resolveCommunityMeta,
} from "./communityDirectoryConfig";

import "./ComingSoonDirectory.css";

/* Same API base pattern as Hub.jsx so both honor the env override. */
const configuredApiBase =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const API_BASE_URL = (
  configuredApiBase || "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const toEntries = (raw) =>
  Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
      ? Object.entries(raw).map(([value, count]) => ({ value, count }))
      : [];

const entryName = (item) => {
  if (typeof item === "string") {
    return item.trim();
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  const candidates = [
    item.value,
    item.community,
    item.name,
    item.label,
    item.slug,
    item.key,
  ];

  const hit = candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );

  return hit ? hit.trim() : "";
};

const entryCount = (item) => {
  if (!item || typeof item !== "object") {
    return 0;
  }

  const count = Number(
    item.count ??
      item.business_count ??
      item.businessCount ??
      item.total ??
      item.totalBusinesses ??
      0
  );

  return Number.isFinite(count) && count >= 0 ? count : 0;
};

/**
 * Coming-soon page for any community or category that has no approved
 * businesses yet.
 *
 *   "List your business"  -> listBusinessTo   (default /partner-signup)
 *   "Suggest a business"  -> suggestBusinessTo (default /contact change
 *                            this to your actual Contact Us route)
 *
 * Works standalone as a routed page (like Ethiopian.jsx) or rendered by
 * ApiCommunityDirectoryPage when the fetch returns zero businesses.
 */
export default function ComingSoonDirectory({
  name,
  kind = "community",
  eyebrow,
  title,
  badgeText = "Coming soon",
  headline,
  body,
  claimText,
  tags = [],
  listBusinessTo = "/partner-signup",
  suggestBusinessTo = "/contact",
  showActiveCommunities = true,
  maxActiveCommunities = 6,
}) {
  const isCommunity = kind === "community";
  const currentSlug = isCommunity ? resolveCommunityMeta(name).slug : "";

  const [liveCommunities, setLiveCommunities] = useState([]);

  const resolvedEyebrow =
    eyebrow || (isCommunity ? `${name} community guide` : `${name} listings`);

  const resolvedTitle =
    title ||
    `${name} favorites, businesses, and local essentials in San Antonio.`;

  const resolvedHeadline =
    headline ||
    (isCommunity
      ? `No ${name} businesses listed yet`
      : `No ${name} listings yet`);

  const resolvedBody =
    body ||
    (isCommunity
      ? `GoOurHub is growing across San Antonio, and this page is saved for the ${name} community. The first business listed here gets the spotlight.`
      : `GoOurHub is growing across San Antonio, and this category is ready for its first listing. The first business listed here gets the spotlight.`);

  const resolvedClaim =
    claimText ||
    (isCommunity
      ? `Own a business in the ${name} community? Listing is free.`
      : `Own a ${name} business? Listing is free.`);

  useEffect(() => {
    if (!showActiveCommunities) {
      return undefined;
    }

    const controller = new AbortController();

    const loadLiveCommunities = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/directory/communities`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          }
        );

        const payload = await response.json();

        if (!response.ok || payload?.success === false) {
          throw new Error("Community directory unavailable.");
        }

        const unique = new Map();

        toEntries(payload?.communities).forEach((item) => {
          const rawName = entryName(item);

          if (!rawName) {
            return;
          }

          const meta = resolveCommunityMeta(rawName);
          const count = entryCount(item);
          const existing = unique.get(meta.slug);

          unique.set(meta.slug, {
            ...meta,
            count: Math.max(existing?.count || 0, count),
          });
        });

        const live = Array.from(unique.values())
          .filter((item) => item.count > 0 && item.slug !== currentSlug)
          .sort((left, right) => right.count - left.count)
          .slice(0, maxActiveCommunities);

        if (!controller.signal.aborted) {
          setLiveCommunities(live);
        }
      } catch (error) {
        /* Fail quiet the static links below still give a way out. */
        if (error.name !== "AbortError" && !controller.signal.aborted) {
          setLiveCommunities([]);
        }
      }
    };

    loadLiveCommunities();

    return () => controller.abort();
  }, [currentSlug, maxActiveCommunities, showActiveCommunities]);

  return (
    <div className="gh-coming-soon">
      <header className="gh-coming-soon__head">
        <span className="gh-coming-soon__eyebrow">{resolvedEyebrow}</span>

        <div className="gh-coming-soon__title-row">
          <h1 className="gh-coming-soon__title">{resolvedTitle}</h1>
          <span className="gh-coming-soon__badge">{badgeText}</span>
        </div>

        {tags.length > 0 && (
          <ul className="gh-coming-soon__tags" aria-label="Page topics">
            {tags.map((tag) => (
              <li key={tag} className="gh-coming-soon__tag">
                {tag}
              </li>
            ))}
          </ul>
        )}
      </header>

      <section
        className="gh-empty"
        aria-label={`${name} has no approved listings yet`}
      >
        <span className="gh-empty__icon" aria-hidden="true">
          <HiOutlineBuildingStorefront />
        </span>

        <h2 className="gh-empty__headline">{resolvedHeadline}</h2>

        <p className="gh-empty__body">{resolvedBody}</p>

        <div className="gh-empty__actions">
          <Link to={listBusinessTo} className="gh-btn gh-btn--primary">
            List your business
            <FaArrowRight aria-hidden="true" />
          </Link>

          <Link to={suggestBusinessTo} className="gh-btn gh-btn--secondary">
            Suggest a business
          </Link>
        </div>

        <p className="gh-empty__note">{resolvedClaim}</p>
      </section>

      {liveCommunities.length > 0 && (
        <section
          className="gh-explore"
          aria-label="Communities with live listings"
        >
          <span className="gh-explore__label">
            Meanwhile, explore live communities
          </span>

          <div className="gh-explore__chips">
            {liveCommunities.map((item) => (
              <Link
                key={item.slug}
                to={communityUrl(item.value)}
                className="gh-explore__chip"
              >
                {item.label}
                <span className="gh-explore__count">{item.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="gh-coming-soon__links">
        <Link to="/categories" className="gh-link-pill">
          Browse all categories
          <FaArrowRight aria-hidden="true" />
        </Link>

        <Link to="/" className="gh-link-pill gh-link-pill--ghost">
          Back to GoOurHub home
        </Link>
      </div>
    </div>
  );
}
